import React, { useRef, useState } from 'react';
import Button from '../../../components/common/Button';
import { isAllowedAttachment } from './agentThread';

export interface ComposerProps {
  disabled?: boolean;
  /** Resolves when the message was accepted; rejects (with a message) to keep the draft. */
  onSend(text: string, files: File[]): Promise<void>;
}

/** Message box with CSV/XLSX attachments. Enter sends; Shift+Enter starts a new line. */
const Composer: React.FC<ComposerProps> = ({ disabled, onSend }) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);

  const addFiles = (list: FileList | null) => {
    const picked = Array.from(list || []);
    const bad = picked.filter((f) => !isAllowedAttachment(f.name));
    setError(bad.length ? `Only CSV, XLSX, TXT and JSON files can be attached: ${bad.map((f) => f.name).join(', ')}` : '');
    setFiles((current) => current.concat(picked.filter((f) => isAllowedAttachment(f.name))));
    if (fileInput.current) fileInput.current.value = '';
  };

  const canSend = !disabled && !sending && (text.trim() !== '' || files.length > 0);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    setError('');
    try {
      await onSend(text.trim(), files);
      setText('');
      setFiles([]);
    } catch (err: any) {
      setError(err?.message || 'Could not send the message');
    } finally {
      setSending(false);
    }
  };

  return (
    <form className="agent-composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
      {files.length > 0 && (
        <div className="agent-attachments">
          {files.map((f, i) => (
            <span key={`${f.name}-${i}`} className="agent-file-chip">
              {f.name}
              <button type="button" className="agent-file-chip__remove" aria-label={`Remove ${f.name}`}
                onClick={() => setFiles((current) => current.filter((_, j) => j !== i))}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className="agent-composer__row">
        <input ref={fileInput} type="file" accept=".csv,.xlsx,.txt,.json" multiple hidden
          aria-label="Attach CSV, XLSX, TXT or JSON files"
          onChange={(e) => addFiles(e.target.files)} />
        <Button variant="ghost" size="md" onClick={() => fileInput.current?.click()} disabled={disabled || sending}>
          Attach
        </Button>
        <textarea
          className="input input--default input--md agent-composer__input"
          aria-label="Message"
          placeholder="Ask anything about iKompete, or attach a CSV…"
          rows={2}
          value={text}
          disabled={disabled}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              send();
            }
          }}
        />
        <Button variant="primary" size="md" type="submit" disabled={!canSend}>Send</Button>
      </div>
      {error && <div className="agent-inline-error" role="status">{error}</div>}
    </form>
  );
};

export default Composer;
